/**
 * Pure helpers for assembling live streaming diarization turns (Azure AI
 * Speech's ConversationTranscriber) into speaker-grouped segments.
 *
 * Kept separate from index.tsx so the merge/format math is unit-testable
 * without an AudioContext / Azure connection. The live-built segments
 * deliberately mirror the backend SegmentedTranscriptEntrySchema so they
 * drop straight into the SAME render path (TranscriptEntry) and persist
 * path (segmented_full_transcript) as the batch-produced ones.
 */

/**
 * True if `label` is a provider's "couldn't confidently attribute this
 * speech to a speaker" placeholder — e.g. a very short or unclear
 * utterance. Compared case-insensitively because the exact casing isn't
 * consistent across providers: Azure's ConversationTranscriber uses
 * "Unknown" (title case — confirmed against Microsoft's real-time
 * diarization docs), while the batch/legacy AssemblyAI convention this
 * code originally targeted used "UNKNOWN" (all-caps). A case-sensitive
 * check would silently let Azure's real placeholder through as if it
 * were a genuine extra speaker.
 */
function isUnattributedSpeaker(label: string): boolean {
  return label.toUpperCase() === "UNKNOWN";
}

export type LiveSegment = {
  speaker: string;
  /** [startSec, endSec] relative to session start. */
  timestamp: [number, number];
  timestamp_hhmmss: [string, string];
  text: string;
};

export type LiveTurn = {
  speaker: string;
  text: string;
  startSec: number;
  endSec: number;
};

/** Format a number of seconds as "HH:MM:SS". */
export function formatHhmmss(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

/**
 * Default heuristic role mapping, applied in order of first appearance.
 * AssemblyAI/Azure assign raw labels ("A"/"Guest-1", ...) in the order
 * speakers are first detected — not by identity — so the convention here is:
 *   1st distinct speaker to talk -> "Examiner" (the doctor typically opens
 *                                    the session)
 *   2nd distinct speaker         -> "Examinee"
 *   3rd+ distinct speaker        -> "Speaker C", "Speaker D", ...
 *
 * This is a HEURISTIC, not an identity — the UI must let the user override
 * individual mappings (see speaker_role_mapping.md, Track A). It should only
 * ever be auto-applied to LIVE diarization output via extendSpeakerRoles —
 * persisted, batch-resolved speaker names (e.g. from the backend's GPT-4o
 * speaker naming) are already better than this guess and must not be
 * overwritten by it.
 *
 * Used as extendSpeakerRoles's fallback `roleOrder` when the caller doesn't
 * have anything better (e.g. the consent modal's checked speaker roles —
 * see AudioRecorder.tsx / SpeakerChecklist.tsx) to pass in.
 */
export const DEFAULT_ROLE_ORDER = ["Examiner", "Examinee"];

export type SpeakerRoleMap = Record<string, string>;

/**
 * Extend a speaker -> role map with any new raw speaker labels found in
 * `segments` that aren't already present in `existingMap`. Existing entries
 * — whether default-derived or user-overridden — are NEVER touched, so a
 * manual rename survives as more live segments arrive.
 *
 * An unattributed-speaker placeholder (see isUnattributedSpeaker — e.g.
 * Azure's "Unknown" for an utterance too short to confidently attribute)
 * never gets a role — left unmapped so callers can render it distinctly
 * rather than assigning it "Examiner"/"Examinee".
 *
 * `maxSpeakers`, when provided, caps how many DISTINCT roles this function
 * will ever mint for a session. Real-time diarization (both AssemblyAI and
 * Azure) can occasionally split a short/unclear utterance into its own
 * phantom extra speaker id even when a speaker-count hint was given (the
 * hint isn't a hard cap on the diarizer's output) — without this cap, that
 * single glitchy fragment claims a role slot and pushes the two REAL
 * speakers down by one (e.g. the doctor ends up labelled "Examinee" and the
 * patient "Speaker C" for a session explicitly configured with
 * maxSpeakers=2). Once the cap is reached, further new labels are left
 * unmapped (rendered as their raw id) rather than confidently — and
 * wrongly — assigned a role. This does not fully prevent the ROLE SWAP
 * itself if the glitch is the very first thing detected (see
 * speaker_role_mapping.md); the manual rename affordance in the UI is the
 * correction path for that.
 *
 * `roleOrder`, when provided, replaces DEFAULT_ROLE_ORDER as the label
 * sequence assigned by first-appearance order. The consent modal's speaker
 * checklist (AudioRecorder.tsx) derives this from the checked speakers'
 * actual display names (e.g. ["Dr. Sarah Johnson", "Madeleine VAN VELZEN",
 * "Interpreter"]) — the same names shown checked in that modal — so a live
 * speaker is named after who's actually in the room instead of a generic
 * "Examiner"/"Speaker C". Still an ORDER heuristic, not an identity —
 * whoever speaks 3rd gets whatever name is 3rd in this list, regardless of
 * which person that voice actually belongs to.
 *
 * Returns the SAME `existingMap` reference when nothing new was found, so
 * callers using this inside a setState updater don't trigger extra renders.
 */
export function extendSpeakerRoles(
  existingMap: SpeakerRoleMap,
  segments: LiveSegment[],
  maxSpeakers?: number,
  roleOrder: string[] = DEFAULT_ROLE_ORDER
): SpeakerRoleMap {
  let map: SpeakerRoleMap | null = null;
  for (const seg of segments) {
    const label = seg.speaker;
    if (isUnattributedSpeaker(label) || label in (map ?? existingMap)) continue;

    const currentCount = Object.keys(map ?? existingMap).length;
    if (maxSpeakers != null && currentCount >= maxSpeakers) continue;

    if (!map) map = { ...existingMap };
    const idx = currentCount;
    map[label] =
      idx < roleOrder.length
        ? roleOrder[idx]
        : `Speaker ${String.fromCharCode(67 + (idx - roleOrder.length))}`;
  }
  return map ?? existingMap;
}

/**
 * Friendly fallback label for a raw LIVE speaker id that extendSpeakerRoles
 * left unmapped — e.g. a voice Azure detected beyond the session's
 * maxSpeakers cap (see extendSpeakerRoles's cap behaviour). Numbers each
 * distinct unmapped raw id in order of first appearance so multiple extras
 * stay distinguishable in the UI ("Unidentified speaker 1", "Unidentified
 * speaker 2") instead of all colliding on the same label.
 *
 * LIVE-ONLY, same restriction as extendSpeakerRoles: only ever call this
 * with LIVE segments. An empty `speakerRoleMap` is normal for a persisted,
 * batch-resolved transcript (no live diarization ran to populate it) — its
 * raw speaker field is already a good, backend-assigned name, and running
 * it through this would wrongly relabel every persisted speaker
 * "Unidentified speaker N" instead of showing their real name.
 */
export function buildUnidentifiedSpeakerLabels(
  segments: LiveSegment[],
  speakerRoleMap: SpeakerRoleMap
): SpeakerRoleMap {
  const labels: SpeakerRoleMap = {};
  let n = 0;
  for (const seg of segments) {
    const raw = seg.speaker;
    if (isUnattributedSpeaker(raw) || raw in speakerRoleMap || raw in labels)
      continue;
    n += 1;
    labels[raw] = `Unidentified speaker ${n}`;
  }
  return labels;
}

/**
 * Resolve the display name for a raw diarization speaker id. This is the
 * ONE lookup chain every UI surface that renders a speaker label must use —
 * role/rename map first, then the "Unidentified speaker N" fallback, then
 * the raw id as a last resort (e.g. a persisted/backend-named speaker,
 * where both maps are legitimately empty).
 *
 * Centralised because this exact chain was duplicated ad hoc across the
 * detailed transcript view, its "View By" filter, and the live-recording
 * feed — and got applied in the first two but forgotten in the third,
 * which is why checked roles ("Examiner"/"Examinee") showed correctly in
 * the Full Transcript view but leaked as raw Azure ids ("Guest-1") in the
 * live feed. Route every new consumer through this function instead of
 * re-deriving the chain.
 */
export function resolveSpeakerDisplay(
  raw: string,
  speakerRoleMap: SpeakerRoleMap,
  unidentifiedSpeakerLabels: SpeakerRoleMap
): string {
  return speakerRoleMap[raw] ?? unidentifiedSpeakerLabels[raw] ?? raw;
}

/**
 * Append a finalized diarization turn to the running segment list.
 *
 * Consecutive turns from the SAME speaker are merged into a single segment:
 * AssemblyAI emits a Turn per clause, so without merging the rendered
 * transcript would be one bubble per sentence. A turn from a different
 * speaker — or the very first turn — opens a new segment.
 *
 * Returns a NEW array (never mutates the input) so it can drive React state.
 * Empty/whitespace-only turns are ignored and the list is returned as-is.
 */
export function appendLiveSegment(
  segments: LiveSegment[],
  turn: LiveTurn
): LiveSegment[] {
  const text = turn.text.trim();
  if (!text) return segments;

  const last = segments[segments.length - 1];
  if (last && last.speaker === turn.speaker) {
    // Same speaker still talking — extend the last segment's text and end.
    const mergedEnd = Math.max(last.timestamp[1], turn.endSec);
    const merged: LiveSegment = {
      speaker: last.speaker,
      text: `${last.text} ${text}`.trim(),
      timestamp: [last.timestamp[0], mergedEnd],
      timestamp_hhmmss: [last.timestamp_hhmmss[0], formatHhmmss(mergedEnd)],
    };
    return [...segments.slice(0, -1), merged];
  }

  const start = Math.max(0, turn.startSec);
  const end = Math.max(start, turn.endSec);
  return [
    ...segments,
    {
      speaker: turn.speaker,
      timestamp: [start, end],
      timestamp_hhmmss: [formatHhmmss(start), formatHhmmss(end)],
      text,
    },
  ];
}
